import React, { FC, useEffect, useMemo, useState } from 'react'
import print from 'styles/components/print.module.scss'
import text from 'styles/utility/text.module.scss'
import format from 'date-fns/format'
import { MainHeadline } from 'components/Typography'
import { PrintOrderCard } from 'components/Card'
import { fetchBaskets, initBaskets, unsubscribeBaskets } from 'reducks/basket/operations'
import { countOrderTimes, fetchOrders, initOrders, unsubscribeOrders } from 'reducks/checkout/operations'
import { useDispatch, useSelector } from 'react-redux'
import { State } from 'index'
import { getStoreRef } from 'reducks/auth/selectors'
import { BasketType } from 'types/basket'
import { getBaskets } from 'reducks/basket/selectors'
import { getOrders, getOrderTimes } from 'reducks/checkout/selectors'
import { Spacer } from 'components/Utility'
import HTMLReactParser from 'html-react-parser'
import { FlexRow } from 'components/Flex'
import { getBrowserName } from 'lib/judge'
import { CHROME_SHEET_HEIGHT, ORDER_HEADER_FOOTER, SAFARI_SHEET_HEIGHT } from 'constants/print'
import { StockUnionType } from 'types/stock'
import { fetchSchedules } from 'reducks/schedule/operations'
import { getSchedules } from 'reducks/schedule/selectors'
import { fetchUsers } from 'reducks/user/operations'
import { getUsers } from 'reducks/user/selectors'
import { OrderTime, SortKey, SortOrder } from 'types/checkout'
import { sortOrdersByKey } from 'lib/order'
import { fetchBreads } from 'reducks/bread/operations'
import { getBreads } from 'reducks/bread/selectors'

type Props = {
  currentDate: Date
  date: string | undefined
  sortKey: SortKey
  sortOrder: SortOrder
  status: string
  title: string
  types: StockUnionType[]
}

const PrintOrderTemplate: FC<Props> = (props) => {
  const { currentDate, date, sortKey, sortOrder, status, title, types } = props
  const dispatch = useDispatch()
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const baskets: BasketType[] = useSelector((state: State) => getBaskets(state))
  const schedules = useSelector((state: State) => getSchedules(state))
  const breads = useSelector((state: State) => getBreads(state))
  const orders = useSelector((state: State) => getOrders(state))
  const orderTimes: OrderTime[] = useSelector((state: State) => getOrderTimes(state))
  const users = useSelector((state: State) => getUsers(state))
  const storeRefs = useMemo(() => [storeRef], [storeRef])

  const [printedCards, setPrintedCards] = useState<JSX.Element[]>([])
  const [isReadyPrint, setReadyPrint] = useState<boolean>(false)
  const isChrome = getBrowserName() === 'chrome'

  // 受取番号に対応するユーザー情報を取得する
  const userIds = useMemo(() => baskets.map((basket) => basket.user_id), [baskets])

  useEffect(() => {
    if (
      orders.length === 0 ||
      baskets.length === 0 ||
      orderTimes.length === 0 ||
      schedules.length === 0 ||
      breads.length === 0
    )
      return
    // レンダリングを待つために1000ミリ秒のwait処理
    setTimeout(() => {
      const documents = Array.from(document.getElementsByClassName('card-parent'))
      if (documents.length === 0) return
      const cards: JSX.Element[] = []
      const SHEET_HEIGHT = isChrome
        ? CHROME_SHEET_HEIGHT - ORDER_HEADER_FOOTER
        : SAFARI_SHEET_HEIGHT - ORDER_HEADER_FOOTER
      let currentHeight = 0
      // 改ページが必要なカードのindexを取得
      const breakBeforePages: number[] = [0]
      for (let i = 0; i < documents.length; i = (i + 1) | 0) {
        const document = documents[i]
        if (!document) continue
        currentHeight += document.clientHeight
        if (currentHeight >= SHEET_HEIGHT) {
          breakBeforePages.push(i)
          currentHeight = document.clientHeight
        }
      }
      for (let i = 0; i < breakBeforePages.length; i = (i + 1) | 0) {
        const startRefIndex = breakBeforePages[i]
        // 最終ページかどうかを判定
        const slicedDocuments =
          i + 1 === breakBeforePages.length
            ? documents.slice(startRefIndex)
            : documents.slice(startRefIndex, breakBeforePages[i + 1])
        const maxLength = slicedDocuments.reduce((prev, document) => {
          const length = document.childNodes[0]?.childNodes[0]?.firstChild?.textContent?.length
          if (!length) return prev
          if (prev > length) return prev
          return length
        }, 0)
        cards.push(
          <section className={isChrome ? print.sheet_chrome : print.sheet_safari}>
            {isChrome ? (
              <Spacer size={49} /> /* Chrome用ヘッダの余白 */
            ) : (
              <>
                <Spacer size={12} />
                <FlexRow justifyContent={'between'}>
                  <MainHeadline headline={title} />
                  <p className={`${text.font_small} ${text.align_right}`}>
                    {format(new Date(), 'yyyy年M月d日HH:mm時点')}
                  </p>
                </FlexRow>
                <Spacer size={12} />
              </>
            )}
            {slicedDocuments.map((document) => {
              if (!document) return
              return HTMLReactParser(document.innerHTML)
            })}
            <div className={print.footer}>
              ページ {i + 1}/{breakBeforePages.length}
            </div>
          </section>
        )
      }
      setPrintedCards([...cards])
      setReadyPrint(true)
    }, 1000)
  }, [orders, baskets, orderTimes, schedules, breads])

  useEffect(() => {
    if (!isReadyPrint) return
    setTimeout(() => {
      if (isChrome) {
        window.print()
      } else {
        document.execCommand('print', false, undefined)
      }
    }, 500)
  }, [isReadyPrint])

  // 日付が変わったら受取番号を取得する
  useEffect(() => {
    if (!date || !storeRef || baskets.length > 0) return
    dispatch(initBaskets())
    dispatch(fetchBaskets(date, storeRef))
    return () => {
      dispatch(unsubscribeBaskets())
    }
  }, [date, storeRef])

  useEffect(() => {
    if (!storeRef || schedules.length > 0) return
    dispatch(fetchSchedules(storeRef))
  }, [storeRef])

  // ステータスまたは注文タイプが変更されたら注文情報を再取得する
  useEffect(() => {
    if (!date || !storeRef || !status || !types || orders.length > 0) return
    dispatch(initOrders())
    dispatch(
      fetchOrders(
        date,
        status,
        types,
        status === 'すべて' ? ['succeeded', 'requires_capture', 'canceled'] : ['succeeded', 'requires_capture'],
        storeRef
      )
    )
    return () => {
      dispatch(unsubscribeOrders())
    }
  }, [date, storeRef, status, types])

  // 注文回数を取得
  useEffect(() => {
    if (
      !currentDate ||
      !storeRefs ||
      !userIds ||
      storeRefs.length === 0 ||
      userIds.length === 0 ||
      orderTimes.length > 0
    )
      return
    dispatch(countOrderTimes(userIds, currentDate, storeRefs))
  }, [userIds, currentDate, storeRefs])

  // 顧客情報を取得
  useEffect(() => {
    if (!userIds || userIds.length === 0 || users.length > 0) return
    dispatch(fetchUsers(userIds))
  }, [userIds])

  // パン情報を取得
  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchBreads(storeRef, 'all', 'name'))
  }, [storeRef])

  return (
    <div>
      <section className={print.screenArea}>
        <Spacer size={12} />
        <FlexRow justifyContent={'between'}>
          <MainHeadline headline={title} />
          <p className={`${text.font_small} ${text.align_right}`}>{format(new Date(), 'yyyy年M月d日HH:mm時点')}</p>
        </FlexRow>
        <Spacer size={12} />
        {baskets.length > 0 &&
          orderTimes.length > 0 &&
          orders.length > 0 &&
          baskets
            .sort((a, b) => {
              // keyと昇順/降順に応じて受取番号の並び替えを行う（デフォルトは注文日時の降順）
              return sortOrdersByKey(sortKey, sortOrder, a, b, orders)
            })
            .map((basket) => {
              const user = users.find((_user) => _user.user_id === basket.user_id)
              if (!user) return
              const _orders = orders
                .filter((order) => order.user_id === basket.user_id)
                .sort((a, b) => {
                  // 来店予定時刻が早い順に並び替えて表示
                  return a.pickup_time < b.pickup_time ? -1 : 1
                })
              if (_orders.length === 0) return
              const orderTime = orderTimes.find((orderTime) => orderTime.user_id === basket.user_id)
              if (!orderTime) return
              return (
                <div className={'card-parent'} key={basket.basket_number}>
                  <PrintOrderCard
                    basketNumber={basket.basket_number}
                    orders={_orders}
                    orderTimes={orderTime.times}
                    user={user}
                  />
                  <Spacer size={12} />
                </div>
              )
            })}
      </section>
      {/* 印刷用エリア(ブラウザでは非表示) */}
      <section className={print.printArea}>
        {isChrome && (
          <div className={print.header}>
            <MainHeadline headline={title} />
            <p className={`${text.font_small} ${text.align_right}`}>{format(new Date(), 'yyyy年M月d日HH:mm時点')}</p>
          </div>
        )}
        {printedCards}
      </section>
    </div>
  )
}

export default PrintOrderTemplate
